import {
  TokenDistributionManager,
  TokenDistributionManager__factory,
} from "@gu-corp/joc-token-manager-contracts";
import { useProvider, useSigner, useStatus } from "@gu-corp/gu-wallet-connector";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { env } from "../libraries/utils/env";
import useIsInTheWrongNetwork from "../hooks/useIsInTheWrongNetwork";

export interface ContractContextType {
  contract: TokenDistributionManager | null;
  metadata: {
    symbol: string;
    decimals: number;
    name: string;
  };
  maximumExtendPeriod: number;
  refetchMaximumExtendPeriod: () => Promise<void>;
}

export const ContractContext = createContext<ContractContextType>({
  contract: null,
  metadata: {
    symbol: "",
    decimals: 18,
    name: "",
  },
  maximumExtendPeriod: 0,
  refetchMaximumExtendPeriod: async () => {},
});

export const ContractProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const status = useStatus();
  const isInTheWrongNetwork = useIsInTheWrongNetwork();
  const isConnected = status === "connected";
  const provider = useProvider();
  const signer = useSigner();
  const contract = useMemo(() => {
    if (!isConnected || isInTheWrongNetwork) {
      return null;
    }
    return TokenDistributionManager__factory.connect(
      env.REACT_APP_CONTRACT_ADDRESS?.toLowerCase() as string,
      signer || provider
    );
  }, [signer, provider, isConnected, isInTheWrongNetwork]);
  const [metadata, setMetadata] = useState({
    symbol: "",
    decimals: 18,
    name: "",
  });
  const [maximumExtendPeriod, setMaximumExtendPeriod] = useState(0);
  const refetchMaximumExtendPeriod = useCallback(async () => {
    if (!contract) return;
    const maximumExtendPeriod = await contract.maximumLockupExtendPeriod();
    setMaximumExtendPeriod(Number(maximumExtendPeriod));
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const maximumExtendPeriod = await contract.maximumLockupExtendPeriod();
      setMaximumExtendPeriod(Number(maximumExtendPeriod));
    })();
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const symbol = await contract.symbol();
      const name = await contract.name();
      const decimals = await contract.decimals();
      setMetadata({
        name,
        symbol,
        decimals: +decimals.toString(),
      });
    })();
  }, [contract]);

  return (
    <ContractContext.Provider
      value={{
        contract,
        metadata,
        maximumExtendPeriod,
        refetchMaximumExtendPeriod,
      }}
    >
      {children}
    </ContractContext.Provider>
  );
};

export const useContract = (): TokenDistributionManager | null => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useContract must be used within a ContractProvider");
  }
  return context.contract;
};

export const useMetadata = (): {
  symbol: string;
  decimals: number;
  name: string;
} => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useMetadata must be used within a ContractProvider");
  }
  return context.metadata;
};

export const useMaximumExtendPeriod = (): {
  data: number;
  refetch: () => Promise<void>;
} => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error(
      "useMaximumExtendPeriod must be used within a ContractProvider"
    );
  }
  return {
    data: context.maximumExtendPeriod,
    refetch: context.refetchMaximumExtendPeriod,
  };
};
